import { CsvTemplateLink } from '../../components/CsvTemplateLink';
import { useState } from 'react';
import type { CsvEntity, ValidationIssue } from '@royaltyflow/validation';
import { FileDropzone, useToast } from '../../components/DesignSystem';
import { ValidationIssuePanel, validationIssues } from '../../components/ValidationIssuePanel';
import { CSV_COLUMNS, CSV_LIMIT, inspectCsvHeader, issue } from '../../lib/csvValidation';
import { errorMessage } from '../../lib/errors';
import { readFileText } from '../../lib/files';

interface CsvImportBoxProps {
  entity: CsvEntity;
  onImport: (csvText: string, fileName: string) => Promise<unknown>;
  successMessage: string;
}
export function CsvImportBox({ entity, onImport, successMessage }: CsvImportBoxProps) {
  const { showToast } = useToast();
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  async function handleFile(file: File) {
    if (isImporting) return;
    setIsImporting(true);
    try {
      if (file.size > CSV_LIMIT) {
        setIssues([issue('csv_limit_exceeded', 'CSV exceeds the 2 MB file limit', entity, '')]);
        return;
      }
      const csvText = await readFileText(file);
      const header = inspectCsvHeader(csvText, entity);
      if (header.issues.length) {
        setIssues(
          header.issues.map((item) => ({
            ...item,
            source: item.source ? { ...item.source, fileName: file.name } : null,
          })),
        );
        return;
      }
      await onImport(csvText, file.name);
      setIssues([]);
      showToast(successMessage);
    } catch (error) {
      setIssues(validationIssues(error));
      showToast(errorMessage(error, 'CSV import failed'), 'error');
    } finally {
      setIsImporting(false);
    }
  }
  return (
    <div>
      <CsvTemplateLink entity={entity} />
      <FileDropzone
        accept=".csv,text/csv"
        hint={`CSV columns: ${[...CSV_COLUMNS[entity].required, ...CSV_COLUMNS[entity].optional].join(', ')}`}
        label={isImporting ? 'Importing…' : 'Drop a CSV here or click to browse'}
        onFile={handleFile}
      />
      <ValidationIssuePanel issues={issues} />
    </div>
  );
}
